/**
 * Pinterest Tag — Enhanced Match helpers.
 *
 * All PII (email/phone) is SHA-256 hashed BEFORE leaving the browser.
 * Pinterest then matches the hash with its own user-graph hashes.
 * We never send raw email/phone — GDPR / CCPA safe.
 *
 * Normalization rules (Pinterest spec):
 *  - email: trim + lowercase + sha256
 *  - phone: digits only, prefix with country code if missing, sha256
 */

const TAG_ID = '2613266545210';

async function sha256(text) {
  if (!text) return undefined;
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest('SHA-256', enc.encode(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function normalizeEmail(email) {
  if (!email) return null;
  const e = String(email).trim().toLowerCase();
  return e.includes('@') ? e : null;
}

function normalizePhone(phone) {
  if (!phone) return null;
  // Keep digits only
  let p = String(phone).replace(/\D/g, '');
  if (!p) return null;
  // Add India country code if 10-digit local
  if (p.length === 10) p = '91' + p;
  return p;
}

export async function buildEnhancedMatch({ email, phone } = {}) {
  const em = await sha256(normalizeEmail(email));
  const ph = await sha256(normalizePhone(phone));
  const out = {};
  if (em) out.em = em;
  if (ph) out.ph = ph;
  return out;
}

/** Fire pintrk event with Enhanced Match data (no-op if tag missing). */
export async function trackPinterest(eventName, eventData = {}, identity = {}) {
  if (typeof window === 'undefined' || !window.pintrk) return;
  try {
    const match = await buildEnhancedMatch(identity);
    window.pintrk('track', eventName, { ...eventData, ...match });
  } catch (e) {
    // Never break the app due to tracking
    console.warn('[pintrk] track failed:', e);
  }
}

/** Re-load the tag with hashed email so PageView gets matched
 *  (called once on app boot when user identity is known). */
export async function setPinterestIdentity({ email, phone } = {}) {
  if (typeof window === 'undefined' || !window.pintrk) return;
  try {
    const match = await buildEnhancedMatch({ email, phone });
    if (Object.keys(match).length === 0) return;
    window.pintrk('load', TAG_ID, match);
    window.pintrk('page');
  } catch (e) {
    console.warn('[pintrk] setIdentity failed:', e);
  }
}
